import { forwardRef, useState, useEffect } from 'react';

import combineClasses from 'classnames';
import styles from '../PrimaryButton/PrimaryButton.module.scss';

interface FontSizeResponsive {
  desktop?: string;
  tablet?: string;
  mobile?: string;
}

interface Props {
  small?: boolean;
  disabled?: boolean;
  orange?: boolean;
  completed?: boolean;
  className?: string;
  secondary?: boolean;
  alert?: boolean;
  alertSecondary?: boolean;
  inverted?: boolean;
  white?: boolean;
  dummy1?: boolean;
  dummy2?: boolean;
  capitalize?: boolean;
  children?: React.ReactNode;

  // Configurable styling props
  backgroundColor?: string;
  hoverColor?: string;
  textColor?: string;
  hoverTextColor?: FontSizeResponsive;
  borderColor?: string;
  hoverBorderColor?: string;
  fontSize?: number;
  fontFamily?: string;
  paddingVertical?: number;
  paddingHorizontal?: number;
  borderRadius?: number;
  width?: number;
  height?: number;

  // Link support
  link?: string;
  openInNewTab?: boolean;

  [k: string]: unknown;
}

const PrimaryButton = forwardRef<HTMLButtonElement, Props>((props: Props, ref) => {
  const {
    small,
    disabled,
    orange,
    completed,
    className,
    secondary,
    alert,
    alertSecondary,
    inverted,
    dummy1,
    dummy2,
    white,
    capitalize,
    children,
    backgroundColor,
    hoverColor,
    textColor,
    hoverTextColor,
    borderColor,
    hoverBorderColor,
    fontSize,
    fontFamily,
    paddingVertical,
    paddingHorizontal,
    borderRadius,
    width,
    height,
    link,
    openInNewTab,
    ...otherProps
  } = props;

  const [isHovered, setIsHovered] = useState(false);
  const [device, setDevice] = useState<'desktop' | 'tablet' | 'mobile'>('desktop');

  // Detect device type by width
  useEffect(() => {
    const updateDevice = () => {
      if (window.innerWidth <= 600) {
        setDevice('mobile');
      } else if (window.innerWidth <= 1024) {
        setDevice('tablet');
      } else {
        setDevice('desktop');
      }
    };
    updateDevice();
    window.addEventListener('resize', updateDevice);
    return () => window.removeEventListener('resize', updateDevice);
  }, []);

  const combinedClasses = combineClasses(
    {
      [`${styles.primaryButton}`]: true,
      [`${styles.small}`]: small,
      [`${styles.disabled}`]: disabled,
      [`${styles.orange}`]: orange,
      [`${styles.white}`]: white,
      [`${styles.inverted}`]: inverted,
      [`${styles.completed}`]: completed,
      [`${styles.secondary}`]: secondary,
      [`${styles.alert}`]: alert,
      [`${styles.alertSecondary}`]: alertSecondary,
      [`${styles.dummy1}`]: dummy1,
      [`${styles.dummy2}`]: dummy2,
      [`${styles.capitalize}`]: capitalize,
    },
    className,
  );

  // Pick hover text color for current device
  const activeHoverTextColor =
    (device === 'desktop' && hoverTextColor?.desktop) ||
    (device === 'tablet' && hoverTextColor?.tablet) ||
    (device === 'mobile' && hoverTextColor?.mobile);

  const buttonStyle: React.CSSProperties = {
    backgroundColor: isHovered && hoverColor ? hoverColor : backgroundColor,
    borderColor: isHovered && hoverBorderColor ? hoverBorderColor : borderColor,
    color: isHovered && activeHoverTextColor ? activeHoverTextColor : textColor || 'white',
    fontSize: fontSize ? `${fontSize}px` : undefined,
    fontFamily: fontFamily || undefined,
    padding: `${paddingVertical ?? 12}px ${paddingHorizontal ?? 24}px`,
    borderRadius: borderRadius !== undefined ? `${borderRadius}px` : '6px',
    width: width ? `${width}px` : 'auto',
    height: height ? `${height}px` : 'auto',
    display: 'inline-flex',
    alignItems: 'center',
    justifyContent: 'center',
    textAlign: 'center',
    transition: 'all 0.3s ease',
  };

  return (
    <>
      {link ? (
        <a
          {...otherProps}
          href={link}
          target={openInNewTab ? '_blank' : undefined}
          rel={openInNewTab ? 'noopener noreferrer' : undefined}
          className={combinedClasses}
          style={buttonStyle}
          onMouseEnter={() => setIsHovered(true)}
          onMouseLeave={() => setIsHovered(false)}
        >
          {children}
        </a>
      ) : (
        <button
          type='button'
          {...otherProps}
          ref={ref}
          disabled={disabled}
          className={combinedClasses}
          style={buttonStyle}
          onMouseEnter={() => setIsHovered(true)}
          onMouseLeave={() => setIsHovered(false)}
        >
          {children}
        </button>
      )}
    </>
  );
});

PrimaryButton.displayName = 'PrimaryButton';
export default PrimaryButton;
